// src/pages/equipmentrentaladmin/dashboard/Dashboard.jsx

import React, { useState, useEffect, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { MdConstruction, MdBuild } from "react-icons/md";
import { FiX, FiCheckCircle } from "react-icons/fi";
import { FaRupeeSign, FaSync } from "react-icons/fa";
import RentalAdminLayout from "../../../layout/equipmentrentaladmin/RentalAdminLayout";
import "../../../assets/css/equipmentrentaladmin/dashboard/RentalDashboard.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

// ── Duration ko sahi unit (Hour/Day/Acre) ke sath dikhane ke liye ──
function formatDuration(duration, rentalbasis) {
  const basis = rentalbasis || "hour";
  const labels = {
    hour: "Hour(s)",
    day: "Day(s)",
    acre: "Acre(s)",
  };
  return `${duration || 0} ${labels[basis] || "Hour(s)"}`;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken, isRentalVerified } = useContext(AppContext);

  // ── States ──
  const [showKycModal, setShowKycModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [dashboardStats, setDashboardStats] = useState({
    totalEquipment: 0,
    pendingRequests: 0,
    totalEarnings: 0,
    underMaintenance: 0,
  });
  const [commissionPercentage, setCommissionPercentage] = useState(0);

  const [activeRentals, setActiveRentals] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);

  // ── Fetch Dashboard Stats (dynamic) ──
  const getDashboardStats = () => {
    axios
      .get(`${baseUrl}/rental-admin/dashboard`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          setDashboardStats(response.data.stats);
          setActiveRentals(response.data.activeRentals || []);
          setMaintenanceRequests(response.data.maintenanceRequests || []);
          setCommissionPercentage(response.data.commissionPercentage || 0); // ← naya
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getDashboardStats();
  }, []);

  useEffect(() => {
    if (!isRentalVerified) {
      setShowKycModal(true);
    }
  }, [isRentalVerified]);

  // ── DashboardStat Data (ab dynamic values se) ──
  const statsData = [
    {
      id: 1,
      icon: <MdConstruction />,
      count: dashboardStats.totalEquipment,
      label: "Total Equipment",
    },
    {
      id: 2,
      icon: <FaSync />,
      count: dashboardStats.pendingRequests,
      label: "Pending Requests",
    },
    {
      id: 3,
      icon: <FaRupeeSign />,
      count: `Rs ${Number(dashboardStats.totalEarnings || 0).toLocaleString()}`,
      label: "Total Earnings",
    },
    {
      id: 4,
      icon: <MdBuild />,
      count: dashboardStats.underMaintenance,
      label: "Under Maintenance",
    },
  ];

  // ── KYC Benefits ──
  const benefits = [
    "Add and manage equipment listings",
    "Receive rental requests from farmers",
    "Withdraw earnings to your account",
    "Build trust with your customers",
  ];

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ================================================================
  // RETURN
  // ================================================================

  return (
    <RentalAdminLayout>
      <div className="rd-page">
        {/* ════════════════════════════════
            Page Header
        ════════════════════════════════ */}

        {/* ── Welcome Banner ── */}
        <div className="rd-welcome-banner">
          <div className="rd-welcome-text">
            <h2 className="rd-welcome-title">
              Welcome Back, Rental Admin! <span>🚜</span>
            </h2>
            <p className="rd-welcome-sub">
              You have{" "}
              <strong>{dashboardStats.pendingRequests} pending requests</strong>{" "}
              waiting for your response.
            </p>
          </div>
          <div className="rd-welcome-icon">
            <MdConstruction />
          </div>
        </div>
        <div className="rd-commission-strip">
          <span className="rd-commission-text">
            You pay{" "}
            <strong>{isLoading ? "..." : `${commissionPercentage}%`}</strong>{" "}
            commission to Smart Agri Rent on every completed booking.
          </span>
        </div>

        {/* ════════════════════════════════
            DashboardStat — 4 cards (dynamic)
        ════════════════════════════════ */}
        <div className="dashboard-stats-container">
          <Row className="g-4">
            {statsData.map((stat) => (
              <Col key={stat.id} xl={3} lg={3} md={6} sm={12}>
                <div
                  className="stat-card"
                  style={{ borderLeft: "4px solid #FDB714" }}
                >
                  <div className="stat-icon">{stat.icon}</div>
                  <div className="stat-content">
                    <h3>{isLoading ? "..." : stat.count}</h3>
                    <p>{stat.label}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* ════════════════════════════════
            Active Rentals (top 2) + Under Maintenance (top 2)
            (graph hata diya gaya hai)
        ════════════════════════════════ */}
        <Row className="g-4">
          {/* Active Rentals */}
          <Col lg={6} md={12}>
            <div className="recent-activity-card">
              <h5>Active Rentals</h5>
              {isLoading ? (
                <p className="rd-empty-text">Loading...</p>
              ) : activeRentals.length === 0 ? (
                <p className="rd-empty-text">No active rentals right now.</p>
              ) : (
                activeRentals.map((item, index) => (
                  <div key={index} className="activity-item">
                    <span className="time">{formatDate(item.bookingdate)}</span>
                    <p>
                      {item.srequipmentmanagment?.equipmentname} —{" "}
                      {item.location} · {formatDuration(item.duration, item.rentalbasis)} · Rs{" "}
                      {Number(item.totalprice || 0).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </Col>

          {/* Under Maintenance */}
          <Col lg={6} md={12}>
            <div className="recent-activity-card">
              <h5>Under Maintenance</h5>
              {isLoading ? (
                <p className="rd-empty-text">Loading...</p>
              ) : maintenanceRequests.length === 0 ? (
                <p className="rd-empty-text">No maintenance requests.</p>
              ) : (
                maintenanceRequests.map((item) => (
                  <div className="activity-item">
                    <span className="time">{item.machinerytype}</span>

                    <p>{item.descriptionissue}</p>

                    <p>{item.location}</p>

                    <p>Mechanic: {item.srmechanicadmin?.fullname}</p>
                  </div>
                ))
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* ════════════════════════════════
          KYC Verification Modal
          ✅ Auto show on dashboard load
      ════════════════════════════════ */}
      {showKycModal && (
        <div className="kyc-overlay" onClick={() => setShowKycModal(false)}>
          <div className="kyc-modal" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              className="kyc-close-btn"
              onClick={() => setShowKycModal(false)}
            >
              <FiX />
            </button>

            {/* Top Section */}
            <div className="kyc-top">
              {/* Icon */}
              <div className="kyc-icon-wrapper">
                <FiCheckCircle size={36} color="#fff" />
              </div>

              <h2 className="kyc-title">Complete Your Verification</h2>

              <p className="kyc-subtitle">
                To start renting on <b>AgriRent</b>, please complete your KYC
                verification. This helps us ensure a safe and trusted
                marketplace for all users.
              </p>

              {/* Benefits Box */}
              <div className="kyc-benefits-box">
                <p className="kyc-benefits-title">Benefits of Verification:</p>
                {benefits.map((benefit, i) => (
                  <div key={i} className="kyc-benefit-item">
                    <div className="kyc-benefit-icon">✅</div>
                    <span className="kyc-benefit-text">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Buttons */}
            <div className="kyc-bottom">
              <button
                className="kyc-verify-btn"
                onClick={() => {
                  setShowKycModal(false);
                  navigate("/rental-admin/profile");
                }}
              >
                Verify Now
              </button>
              <button
                className="kyc-skip-btn"
                onClick={() => setShowKycModal(false)}
              >
                Skip for Now
              </button>
              <p className="kyc-note">
                You can complete verification anytime from Settings → Documents
              </p>
            </div>
          </div>
        </div>
      )}
    </RentalAdminLayout>
  );
};

export default Dashboard;